import type { MockServer } from './server'
import { randBoolean, randMask, randCompanyName } from '@ngneat/falso'

const FINNISH_INSURANCE_REGULATIONS = [
  {
    id: 'lahitapiola_insurance_company_id',
    info: 'Company ID for LahiTapiola insurance',
    value: 100001,
  },
  {
    id: 'if_insurance_company_id',
    info: 'Company ID for IF insurance',
    value: 100002,
  },
  {
    id: 'if_insurance_number_valid_pattern',
    info: 'Regex for IF insurance number validation',
    value: '^(201|205)\\d{7}$',
  },
  {
    id: 'if_insurance_verification_field_id',
    info: 'Custom field ID for IF verification checkbox',
    value: 100003,
  },
]

const LAHITAPIOLA_INSURANCE_COMPANY_ID = FINNISH_INSURANCE_REGULATIONS.find(
  (regulation) => regulation.id === 'lahitapiola_insurance_company_id',
)?.value

const IF_INSURANCE_COMPANY_ID = FINNISH_INSURANCE_REGULATIONS.find(
  (regulation) => regulation.id === 'if_insurance_company_id',
)?.value

export function routes(server: MockServer) {
  server.get('/ui_configs/patients/:config_id', function (schema, request) {
    const hasFinnishInsuranceRegulations =
      schema.first('mergedSettings')?.attrs?.invoice_country === 'FI'
    const insuranceCompanyOptions = hasFinnishInsuranceRegulations
      ? [
          {
            value: LAHITAPIOLA_INSURANCE_COMPANY_ID,
            label: 'LahiTapiola',
          },
          {
            value: IF_INSURANCE_COMPANY_ID,
            label: 'IF Insurance',
          },
          {
            value: Number(randMask({ mask: '######' })),
            label: randCompanyName(),
          },
        ]
      : [
          {
            value: Number(randMask({ mask: '######' })),
            label: 'LahiTapiola',
          },
          {
            value: Number(randMask({ mask: '######' })),
            label: 'IF Insurance',
          },
          {
            value: Number(randMask({ mask: '######' })),
            label: randCompanyName(),
          },
        ]

    const visible = [
      'additional_identification',
      'animal_code',
      'blood_group',
      'breed',
      'color',
      'cpr_status',
      'date_of_birth',
      'date_of_death',
      'date_of_registration',
      'gender',
      'holding_place_number',
      'home_department',
      'insurance',
      'insurance_company',
      'microchip',
      'microchip_date',
      'name',
      'not_for_food',
      'official_name',
      'passport_given',
      'passport_number',
      'private',
      'register_number',
      'remarks',
      'species',
      'stable_name',
      'tags',
      'vetenvoy_activated',
      'vetenvoy_is_breeder',
      'vetenvoy_provider',
      'visible_department_groups',
      'visible_departments',
    ]
    const required = ['gender', 'home_department', 'name', 'species']
    const hidden = [
      'avatar',
      'patient_types',
      'primary_clinic_location',
      'reason_of_death',
      'statuses',
    ]
    const disabled: string[] = []

    const labels = [
      { name: 'name', label: 'Name' },
      { name: 'official_name', label: 'Official name' },
      { name: 'cpr_status', label: 'CPR status' },
      { name: 'date_of_birth', label: 'Date of birth' },
      { name: 'date_of_registration', label: 'Date of registration' },
      { name: 'not_for_food', label: 'Not for food production' },
      { name: 'animal_code', label: 'Animal code' },
      { name: 'species', label: 'Species' },
      { name: 'breed', label: 'Breed' },
      { name: 'gender', label: 'Gender' },
      { name: 'blood_group', label: 'Blood group' },
      { name: 'color', label: 'Color' },
      { name: 'additional_identification', label: 'Additional identifier' },
      { name: 'stable_name', label: 'Stable / Yard name' },
      { name: 'holding_place_number', label: 'Holding place number' },
      { name: 'statuses', label: 'Statuses' },
      { name: 'date_of_death', label: 'Date of death' },
      { name: 'microchip', label: 'Microchip' },
      { name: 'microchip_date', label: 'Microchip date' },
      { name: 'passport', label: 'Passport' },
      { name: 'passport_date', label: 'Passport date' },
      { name: 'register_number', label: 'Register number' },
      { name: 'insurance_company', label: 'Insurance company' },
      {
        name: 'insurance_excess_fee_type',
        label: 'Insurance excess fee type',
      },
      {
        name: 'insurance_excess_fee_amount',
        label: 'Insurance excess fee amount',
      },
      {
        name: 'insurance_policy_end_date',
        label: 'Insurance policy end date',
      },
    ]

    return {
      data: {
        type: 'UIConfig',
        id: `post_patient_${request.queryParams['filter[client_id]']}`,
        attributes: {
          resource_type: 'UIConfigPostPatient',
          resource_required: false,
          resource_id: 0,
          config: {
            id: 'post_patient',
            forms: [
              {
                id: 'post_patient',
                hidden,
                visible,
                required,
                disabled,
                labels,
                initials: {
                  gender: 'UNKNOWN',
                  home_department: '1',
                  private: true,
                  vetenvoy_is_breeder: false,
                  visible_departments: ['1', '2', '3'],
                  visible_department_groups: ['1', '3', '5'],
                },
                options: {
                  vetenvoy_provider: [
                    { value: 1, label: randCompanyName() },
                    { value: 2, label: randCompanyName() },
                    { value: 3, label: randCompanyName() },
                  ],
                  holding_place_number: [
                    { value: 1, label: '1001' },
                    { value: 2, label: '1002' },
                    { value: 3, label: '1003' },
                  ],
                  insurance_company: insuranceCompanyOptions,
                  insurance_excess_fee_type: [
                    { value: 'PERCENTAGE', label: 'Percentage' },
                    { value: 'FLAT_SUM', label: 'Flat sum' },
                  ],
                },
                help_text: {
                  animal_code:
                    'Unique animal identification number, e.g. in EU country code + 12 digits.',
                },
              },
            ],
            controls: null,
            regulations: [
              {
                id: 'microchip_max_length',
                info: 'Maximum length of the microchip number',
                value: 15,
              },
              {
                id: 'require_microchip_date_if_provided',
                info: 'Require microchip date only if microchip is provided',
                value: true,
              },
              {
                id: 'kennel_club_species_id',
                info: 'Species ID that can use the Kennel Club lookup.',
                value: 1,
              },
              {
                id: 'cat_federation_species_id',
                info: 'Species ID that can use the Cat Federation lookup.',
                value: 2,
              },
              ...(hasFinnishInsuranceRegulations
                ? FINNISH_INSURANCE_REGULATIONS
                : []),
            ],
            sections: null,
          },
        },
      },
    }
  })

  server.get('/ui_configs/clients/:config_id', function (schema, request) {
    const mergedSettings = schema.first('mergedSettings')
    const clientTitleAvailable =
      mergedSettings?.attrs?.client_title_available !== false

    const hidden = ['country_region', 'einvoice_address', 'einvoice_operator']
    const visible = [
      'firstname',
      'lastname',
      'home_department',
      'secondary_owner_first_name',
      'secondary_owner_last_name',
      'organization_name',
      'customer_type',
      'street_address',
      'street_address_2',
      'street_address_3',
      'zip_code',
      'city',
      'country',
      'email',
      'phone',
      'alt_emails',
      'id_number',
      'critical_notes',
      'remarks',
      'tags',
      'vat_number',
      'register_number',
      'referring_organization',
      'referring_vet',
      'parent_referring_organization',
      'breeder',
      'production_animal_client',
      'farm_code',
      'holding_place_numbers',
      'invoicing_client',
      'no_sms',
      'no_email',
      'on_stop',
      'archived',
      'state',
      'gender',
      'date_of_birth',
      'drivers_licence_number',
      'status_type',
      'due_date_delay',
      // Communication Preferences
      'communication_preference_valid_from',
      'communication_preference_consent',
      'communication_preference_privacy_policy_version',
      'communication_preference_1',
      'communication_preference_2',
    ]

    const disabled = [
      'communication_preference_3', // Non-editable row
    ]

    if (clientTitleAvailable) {
      visible.push('title')
    } else {
      hidden.push('title')
    }

    return {
      data: {
        type: 'UIConfig',
        id: request.params.config_id,
        attributes: {
          resource_type: 'UIConfigPostClient',
          resource_required: false,
          resource_id: 0,
          config: {
            id: request.params.config_id,
            forms: [
              {
                id: request.params.config_id,
                hidden,
                visible,
                required: randBoolean()
                  ? ['lastname', 'home_department']
                  : ['lastname'],
                disabled,
                labels: [
                  { name: 'firstname', label: 'First name' },
                  { name: 'lastname', label: 'Last name' },
                  {
                    name: 'secondary_owner_first_name',
                    label: 'Secondary owner first name',
                  },
                  {
                    name: 'secondary_owner_last_name',
                    label: 'Secondary owner last name',
                  },
                  { name: 'home_department', label: 'Home department' },
                  { name: 'title', label: 'Title' },
                  { name: 'organization_name', label: 'Organisation name' },
                  { name: 'customer_type', label: 'Customer type' },
                  { name: 'street_address', label: 'Street address' },
                  { name: 'street_address_2', label: 'Second line' },
                  { name: 'street_address_3', label: 'Third line' },
                  { name: 'zip_code', label: 'Post code' },
                  { name: 'city', label: 'City' },
                  { name: 'country', label: 'Country' },
                  { name: 'state', label: 'State' },
                  { name: 'country_region', label: 'Province' },
                  { name: 'email', label: 'Email' },
                  { name: 'phone', label: 'Phone number' },
                  { name: 'alt_emails', label: 'Additional emails' },
                  { name: 'id_number', label: 'ID number' },
                  { name: 'critical_notes', label: 'Critical notes' },
                  { name: 'remarks', label: 'Remarks' },
                  { name: 'tags', label: 'Tags' },
                  { name: 'vat_number', label: 'VAT number' },
                  { name: 'register_number', label: 'Register number' },
                  { name: 'due_date_delay', label: 'Due date delay' },
                  { name: 'status_type', label: 'Client type' },
                  { name: 'gender', label: 'Gender' },
                  { name: 'date_of_birth', label: 'Date of birth' },
                  {
                    name: 'drivers_licence_number',
                    label: "Driver's license number",
                  },
                  {
                    name: 'production_animal_client',
                    label: 'Production animal client',
                  },
                  { name: 'farm_code', label: 'Unique farm code' },
                  {
                    name: 'holding_place_numbers',
                    label: 'Holding place number',
                  },
                  {
                    name: 'einvoice_address',
                    label: 'E-invoice electronic address',
                  },
                  {
                    name: 'einvoice_operator',
                    label: 'E-invoice operator identifier',
                  },
                  {
                    name: 'referring_organization',
                    label: 'Referring organization',
                  },
                  { name: 'referring_vet', label: 'Referring veterinarian' },
                  {
                    name: 'parent_referring_organization',
                    label: 'Linked referring organization',
                  },
                  { name: 'breeder', label: 'Breeder' },
                  { name: 'invoicing_client', label: 'Invoicing client' },
                  { name: 'no_sms', label: 'Disable SMS sending' },
                  { name: 'no_email', label: 'Disable email sending' },
                  { name: 'on_stop', label: 'On stop' },
                  { name: 'archived', label: 'Archived' },
                  // Communication Preferences
                  {
                    name: 'communication_preference_valid_from',
                    label: 'Valid from',
                  },
                  {
                    name: 'communication_preference_consent',
                    label: 'Consent method',
                  },
                  {
                    name: 'communication_preference_privacy_policy_version',
                    label: 'Privacy policy version',
                  },
                  {
                    name: 'communication_preference_1',
                    label: 'Marketing communications',
                  },
                  {
                    name: 'communication_preference_2',
                    label: 'Appointment reminders',
                  },
                  {
                    name: 'communication_preference_3',
                    label: 'Essential service updates',
                  },
                ],
                initials: {
                  state: 'FL',
                  // Communication Preferences initials
                  communication_preference_1: {
                    sms: true,
                    email: true,
                    phone: false,
                    post: false,
                  },
                  communication_preference_2: {
                    sms: true,
                    email: true,
                    phone: true,
                    post: false,
                  },
                  communication_preference_3: {
                    sms: true,
                    email: false,
                    phone: true,
                    post: false,
                  },
                },
                options: {
                  customer_type: [
                    { value: 'CUSTOMER_TYPE_PRIVATE', label: 'Private person' },
                    { value: 'CUSTOMER_TYPE_COMPANY', label: 'Company' },
                    { value: 'CUSTOMER_TYPE_EU_COMPANY', label: 'EU Company' },
                    {
                      value: 'CUSTOMER_TYPE_CHARITABLE_ORG',
                      label: 'Charitable organization',
                    },
                    { value: 'CUSTOMER_UK_CHARITY', label: 'UK charity' },
                  ],
                  communication_preference_consent: [
                    { value: 'FACE_TO_FACE', label: 'Face to face' },
                    { value: 'EMAIL', label: 'Email' },
                    { value: 'TELEPHONE', label: 'Telephone' },
                    { value: 'LEGACY_SYSTEM', label: 'Legacy system' },
                    { value: 'SELF_SERVICE', label: 'Self service' },
                  ],
                },
                help_text: {
                  firstname: 'Legal first name of the client',
                  lastname: 'Legal last name of the client',
                  organization_name: 'Legal name of the organization',
                  id_number:
                    'National ID number used for identification purposes',
                  register_number: 'Company registration number',
                  vat_number: 'VAT identification number',
                  email: 'Primary email address for communication',
                  phone: 'Primary phone number for contact',
                  customer_type: 'Select the type of customer',
                  // Communication Preferences help text with styles
                  communication_preference_1: {
                    description:
                      'We wish to reach out to you to provide you with a range of offerings over your preferred communication preferences.',
                    style: 'SHOW_FULL_TEXT',
                  },
                  communication_preference_2: {
                    description:
                      'Important reminders about upcoming appointments and vaccinations',
                    style: 'SHOW_FULL_TEXT',
                  },
                  communication_preference_3: {
                    description:
                      'Critical service updates and emergency notifications (cannot be disabled)',
                    style: 'SHOW_ON_HOVER',
                  },
                },
              },
            ],
            controls: null,
            regulations: [
              {
                id: 'invoice_country',
                info: 'Controls country-specific behaviors: address field ordering (US/CA format vs European), and special widgets (postcode, tax code calculator, lookup tools)',
                value: 'US',
              },
              {
                id: 'phone_number_prefix',
                info: 'Auto-fills phone number prefix when adding new phone number fields',
                value: '+358',
              },
              {
                id: 'require_full_name_or_organization_name',
                info: 'Either full name (firstname and lastname) or organization name must be filled (higher priority)',
                value: false,
              },
              {
                id: 'require_lastname_or_organization_name',
                info: 'Either lastname or organization name must be filled (lower priority)',
                value: true,
              },
              {
                id: 'required_tags',
                info: 'Dict of tag ID to tag name. Client must have at least one of these tags if not empty.',
                value: {},
              },
              {
                id: 'require_id_or_register_number',
                info: 'If customer type is private person or None, ID number must be provided. If customer type is company, register number must be provided.',
                value: false,
              },
              {
                id: 'check_age',
                info: 'Validates client is 18+ years old if ID number is provided',
                value: false,
              },
              {
                id: 'check_id_format',
                info: 'Validates ID number format if it is provided',
                value: false,
              },
              {
                id: 'soft_check_id_number',
                info: 'Checks ID number and shows warnings if it is not correct, does not block the client creation',
                value: true,
              },
              {
                id: 'use_ratsit_id_number',
                info: 'Enables Ratsit ID number lookup widget',
                value: false,
              },
              {
                id: 'use_ratsit_phone_number',
                info: 'Enables Ratsit phone number lookup widget',
                value: false,
              },
              // Communication Preferences regulations
              {
                id: 'use_communication_preference',
                info: 'Show the Communication Preference section if true',
                value: true,
              },
              {
                id: 'communication_preference_section_guidelines',
                info: 'Guidelines text displayed under the section title',
                value:
                  "To help you take the best care of your pet we'd like to contact you by email, post, text, and phone about services, offers, products and our charity activities.\n\nYou can change your mind at any time here in practice or by unsubscribing from any communications you receive.\n\nAre you happy to be contacted?",
              },
              {
                id: 'default_communication_preference_valid_duration',
                info: 'Default valid duration in months (1-120)',
                value: 24,
              },
            ],
            sections: null,
          },
        },
      },
    }
  })

  server.get(
    '/consultations/:parent_lookup_consultation/ui_configs/:id',
    function (_db, request) {
      return {
        data: {
          type: 'UIConfig',
          id: request.params.id,
          attributes: {
            resource_type: null,
            resource_required: false,
            resource_id: null,
            config: {
              id: 'consultation-tabs',
              forms: null,
              controls: [
                {
                  id: 'internal_remarks',
                  link: null,
                  options: null,
                },
                {
                  id: 'ai_patient_history_summary',
                  link: null,
                  options: null,
                  enabled: true,
                },
              ],
              sections: [
                {
                  id: 'overview',
                  forms: [
                    {
                      id: 'consultation-overview-form',
                      hidden: [],
                      visible: [
                        'started',
                        'admitted_time',
                        'ended',
                        'consultation_type',
                        'patient_type',
                        'complaint',
                        'complaint_type',
                        'ward',
                        'preliminarynote',
                        'reporting_dimension_1',
                        'reporting_dimension_2',
                        'patient_location',
                        'patient_location_2',
                        'supervising_veterinarian',
                        'responsible_nurses',
                        'responsible_surgeons',
                        'referring_organization',
                        'referring_vet',
                        'upper_spend_limit',
                      ],
                      labels: [
                        {
                          name: 'ward',
                          label: 'Service',
                        },
                        {
                          name: 'supervising_veterinarian',
                          label: 'Vet',
                        },
                      ],
                      required: [
                        'started',
                        'admitted_time',
                        'complaint',
                        'complaint_type',
                        'consultation_type',
                        'supervising_veterinarian',
                      ],
                      disabled: [],
                    },
                  ],
                  controls: [
                    {
                      id: 'edit',
                      link: null,
                      options: null,
                    },
                  ],
                },
                {
                  id: 'clinical-notes',
                  forms: null,
                  controls: [
                    {
                      id: 'list',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'edit',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'drafts',
                      link: null,
                      options: null,
                    },
                  ],
                  sections: null,
                },
                {
                  id: 'discharge-instructions',
                  forms: null,
                  controls: [
                    {
                      id: 'ai_summary',
                      link: null,
                      options: null,
                      enabled: true,
                      helper_text:
                        "You've reached your monthly limit of 50 discharge instructions. Your limit will reset on Feb 1, 2026.",
                    },
                    {
                      id: 'email',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'edit',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'edit-reason',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'drafts',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'print',
                      link: null,
                      options: null,
                    },
                  ],
                },
                {
                  id: 'diagnoses',
                  forms: null,
                  controls: [
                    {
                      id: 'previous_diagnoses',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'add_new',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'add_from_previous',
                      link: null,
                      options: null,
                    },
                  ],
                },
                {
                  id: 'diagnostics',
                  forms: null,
                  controls: null,
                  sections: [
                    {
                      id: 'diagnostic-imaging',
                      forms: null,
                      controls: [
                        {
                          id: 'add_from_worklist',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'answer_remarks',
                          link: null,
                          options: null,
                        },
                      ],
                      sections: null,
                    },
                    {
                      id: 'laboratory',
                      forms: null,
                      controls: [
                        {
                          id: 'add_new',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'add_results',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'referral_details_link',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'print',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'analysis_remarks',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'internal_status',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'has_previous_laboratory_referrals',
                          link: null,
                          options: null,
                        },
                      ],
                      sections: null,
                    },
                  ],
                },
                {
                  id: 'vitals',
                  forms: null,
                  controls: [
                    {
                      id: 'list',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'add_new',
                      link: null,
                      options: null,
                      enabled: true,
                    },
                    {
                      id: 'edit',
                      link: null,
                      options: null,
                      enabled: true,
                    },
                  ],
                  sections: null,
                },
                {
                  id: 'treatment-items',
                  forms: null,
                  controls: null,
                  sections: [
                    {
                      id: 'add_items',
                      forms: null,
                      controls: [
                        {
                          id: 'previous_items',
                          link: null,
                          options: null,
                        },
                      ],
                      sections: null,
                    },
                    {
                      id: 'treatment_plans',
                      forms: null,
                      controls: [],
                      sections: null,
                    },
                    {
                      id: 'prescriptions',
                      forms: null,
                      controls: [
                        {
                          id: 'general_presc',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'e_presc',
                          link: null,
                          options: null,
                        },
                      ],
                      sections: null,
                    },
                  ],
                },
                {
                  id: 'estimates',
                  forms: null,
                  controls: [
                    {
                      id: 'edit',
                      link: null,
                      options: null,
                    },
                  ],
                  sections: null,
                },
              ],
            },
          },
        },
      }
    },
  )
}
